import { useQuery } from '@tanstack/react-query';

import { useVendor } from '@/contexts/VendorContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { RuntimeAdvisoriesFlagStatus } from '@/hooks/useRuntimeAdvisoriesFlag';
import { VENDOR_ADVISORIES_SHELL_QUERY_KEY } from '@/lib/advisoryQueries';
import { getVendorAdvisoriesShell } from '@/lib/runtimeAdvisories';
import type { FeatureReleaseStage } from '@/components/ui/feature-stage-badge';

export interface VendorAdvisoriesShellState {
  /** True only once the server resolved the supplier-shell entry ON for this user. */
  enabled: boolean;
  /** True while the user or the server answer is still loading: render nothing, never redirect. */
  loading: boolean;
  unread: number;
  releaseStage: FeatureReleaseStage;
  status: RuntimeAdvisoriesFlagStatus;
  retry: () => void;
}

/** ENT-6091: the server authorises the open profile and resolves its claimant's flag.
 * Vendor-only team members use the same resource boundary as claimant-org members.
 */
export function useVendorAdvisoriesShell(): VendorAdvisoriesShellState {
  const { user, isLoading: organizationLoading } = useOrganization();
  const userId = user?.id ?? null;
  const { vendorProfile, loading: vendorLoading, requestedAdvisoryProfileId } = useVendor();
  const vendorProfileId = vendorProfile?.id ?? null;
  const profilePending = Boolean(requestedAdvisoryProfileId && requestedAdvisoryProfileId !== vendorProfileId);
  const query = useQuery({
    queryKey: [VENDOR_ADVISORIES_SHELL_QUERY_KEY, userId, vendorProfileId],
    queryFn: () => getVendorAdvisoriesShell(vendorProfileId ?? ''),
    enabled: Boolean(userId && vendorProfileId) && !organizationLoading && !vendorLoading && !profilePending,
    staleTime: 60_000,
    retry: false,
  });

  const retry = () => { void query.refetch(); };
  if (organizationLoading || vendorLoading || profilePending) return { enabled: false, loading: true, unread: 0, releaseStage: null, status: 'loading', retry };
  if (!userId || !vendorProfileId) return { enabled: false, loading: organizationLoading || vendorLoading, unread: 0, releaseStage: null, status: organizationLoading || vendorLoading ? 'loading' : 'resolved', retry };
  if (query.isError) return { enabled: false, loading: false, unread: 0, releaseStage: null, status: 'error', retry };
  if (!query.isSuccess) return { enabled: false, loading: true, unread: 0, releaseStage: null, status: 'loading', retry };
  const data = query.data;
  // A transport success is not a resolved flag: both RPCs return a boolean
  // exposure and a nonnegative count(*). Unknown data must preserve the CTA for Retry.
  if (!data || typeof data !== 'object' || Array.isArray(data)
    || typeof data.enabled !== 'boolean' || typeof data.unread !== 'number'
    || !Number.isInteger(data.unread) || data.unread < 0) {
    return { enabled: false, loading: false, unread: 0, releaseStage: null, status: 'error', retry };
  }
  const releaseStage: FeatureReleaseStage = data.release_stage === 'new' || data.release_stage === 'alpha' || data.release_stage === 'beta' ? data.release_stage : null;
  return { enabled: data.enabled, loading: false, status: 'resolved', retry, unread: data.enabled ? data.unread : 0, releaseStage: data.enabled ? releaseStage : null };
}
